import { EServiceTypeNumber } from '@aca-new/app/shared/models/enums/service-type-number.enum';
import { EServiceType } from '@aca-new/app/shared/models/enums/service-type.enum';

export enum EFoodType {
  INDUSTRY = 'industry',
}

/**
 * Single certification outcome (grade) for a food scheme: key, display label, and whether it grants certification.
 */
export interface IFoodCertificationOutcome {
  /** Whether this outcome grants certification. */
  grant: boolean;
  /** Backend/key value (e.g. CERTIFIED, EXCELLENT). */
  key: string;
  /** Display label (e.g. Certified, Excellent). */
  label: string;
}

export interface IIndustrySiteCodeConfig {
  code: string;
  // used for site code input in app-food-legal-entity-information
  labelKey: string;
  lowerKey: string;
  // used for combine add booking navigate
  path?: string;
  // used for site code input in app-food-legal-entity-information
  placeholderKey: string;
  // used for booking step two
  stepTwo?: {
    productScope?: {
      label: string;
    };
  };
  type: EFoodType.INDUSTRY | null;
}

/**
 * Type guard: true if the service type is configured in FOOD_SERVICE_CONFIG_MAP (WQS/food certification cancel flow).
 * @param {string} serviceType - The service type string to check.
 * @returns {boolean} True if the service type is a key in FOOD_SERVICE_CONFIG_MAP.
 */
export function isFoodServiceConfigType(serviceType: string): serviceType is EServiceType {
  return Object.prototype.hasOwnProperty.call(FOOD_SERVICE_CONFIG_MAP, serviceType);
}

export const FOOD_SERVICE_CONFIG_MAP: Partial<Record<EServiceType, IIndustrySiteCodeConfig>> = {
  [EServiceType.GGAP]: {
    code: EServiceTypeNumber.GGAP,
    labelKey: '',
    lowerKey: EServiceType.GGAP.toLowerCase(),
    path: '/book-food-certification/ggap/null',
    placeholderKey: '',
    type: null,
  },
  [EServiceType.BRCGS]: {
    code: EServiceTypeNumber.BRCGS,
    labelKey: 'BOOKS.FOOD_CERTIFICATION.BRCGS_CODE',
    lowerKey: EServiceType.BRCGS.toLowerCase(),
    path: '/book-food-certification/brcgs/:id',
    placeholderKey: 'BOOKS.FOOD_CERTIFICATION.BRCGS_CODE_PLACEHOLDER',
    type: EFoodType.INDUSTRY,
  },
  [EServiceType.SQF]: {
    code: EServiceTypeNumber.SQF,
    labelKey: 'BOOKS.FOOD_CERTIFICATION.SQF_ID',
    lowerKey: EServiceType.SQF.toLowerCase(),
    path: '/book-food-certification/sqf/:id',
    placeholderKey: 'BOOKS.FOOD_CERTIFICATION.SQF_ID_PLACEHOLDER',
    stepTwo: {
      productScope: {
        label: 'BOOKS.FOOD_CERTIFICATION.PROD_PROCESS.FOOD_SAFETY_CODES',
      },
    },
    type: EFoodType.INDUSTRY,
  },
  [EServiceType.IFS]: {
    code: EServiceTypeNumber.IFS,
    labelKey: 'BOOKS.FOOD_CERTIFICATION.COID',
    lowerKey: EServiceType.IFS.toLowerCase(),
    path: '/book-food-certification/ifs/:id',
    placeholderKey: 'BOOKS.FOOD_CERTIFICATION.COID_PLACEHOLDER',
    stepTwo: {
      productScope: {
        label: 'BOOKS.FOOD_CERTIFICATION.PROD_PROCESS.PROD_SCOPE',
      },
    },
    type: EFoodType.INDUSTRY,
  },
};

/**
 * Certification outcomes (grade + grant) per food scheme.
 * Use for lookup by outcome key to get label and whether it grants certification.
 */
export const FOOD_CERTIFICATION_OUTCOME_MAP: Partial<Record<EServiceType, ReadonlyArray<IFoodCertificationOutcome>>> = {
  [EServiceType.BRCGS]: [
    { key: 'CERTIFIED', label: 'Certified', grant: true },
    { key: 'NON_CERTIFIED', label: 'Non-certified', grant: false },
  ],
  [EServiceType.SQF]: [
    { key: 'EXCELLENT', label: 'Excellent', grant: true },
    { key: 'GOOD', label: 'Good', grant: true },
    { key: 'ACCEPTABLE', label: 'Acceptable', grant: true },
    { key: 'FAIL', label: 'Fail', grant: false },
  ],
  [EServiceType.IFS]: [
    { key: 'HIGHER_LEVEL', label: 'Higher level', grant: true },
    { key: 'FOUNDATION', label: 'Foundation', grant: true },
    { key: 'NOT_PASSED', label: 'Not passed', grant: false },
    { key: 'FOLLOW_UP_REQUIRED', label: 'Follow-up required', grant: false },
  ],
  [EServiceType.GGAP]: [
    { key: 'CERTIFIED', label: 'Certified', grant: true },
    { key: 'CANCELLED', label: 'Cancelled', grant: false },
    { key: 'SUSPENDED', label: 'Suspended', grant: false },
    { key: 'SUSPENDED_PENDING_CAS', label: 'Suspended Pending CAs', grant: false },
    { key: 'NONE', label: 'None', grant: false },
    { key: 'PENDING', label: 'Pending', grant: false },
  ],
};

export const INDUSTRY_SERVICE_TYPES: EServiceType[] = [EServiceType.BRCGS, EServiceType.IFS, EServiceType.SQF];

export const AGRICULTURE_SERVICE_TYPES: EServiceType[] = [EServiceType.GGAP];

/**
 * Industry service types that support combine booking (add to booking on confirmation page).
 * For these types, form fields are disabled when the order is combined with another order.
 * SQF is industry but does not support combine; only BRCGS and IFS do.
 */
export const INDUSTRY_SERVICE_TYPES_COMBINE_ELIGIBLE: EServiceType[] = [EServiceType.BRCGS, EServiceType.IFS];
